import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isNotNullOrUndefined, SingleSelect } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { SingleSelectFilterData } from '../../store/states/filterTypes';

import { FilterFormatter, mapFilterOptionToSelectData } from './formatter';
import { FILTER_DYNAMIC_WIDTH_SETTINGS } from './util';

export type SingleSelectFilterProps = {
  filterData?: SingleSelectFilterData;
  entityId: string;
  filterKey: string;
  formatter: FilterFormatter;
  isFetching: boolean;
  clearable?: boolean;
  disableDynamicWidth?: boolean;
  label?: ReactNode | ReactNode[];
};
export function SingleSelectFilter({
  filterData,
  entityId,
  filterKey,
  formatter,
  isFetching,
  label,
  clearable,
  disableDynamicWidth = false,
}: SingleSelectFilterProps) {
  const { t } = useTranslation();
  const [setFilterSelectionValue, resetFilterSelections] = useStore(
    useShallow((state) => [
      state.setFilterSelectionValue,
      state.resetFilterSelections,
    ])
  );

  const missingOptionsError = !isFetching && !filterData?.data?.length;

  const data =
    filterData?.data
      ?.map((option) => mapFilterOptionToSelectData(option, formatter))
      .filter(isNotNullOrUndefined) ?? [];

  if (missingOptionsError) {
    console.warn(
      `There are no options available for '${filterKey}'. This is a data or configuration error.`
    );
  }

  return (
    <SingleSelect
      data={data}
      key={filterKey}
      dynamicWidthSettings={
        disableDynamicWidth ? undefined : FILTER_DYNAMIC_WIDTH_SETTINGS
      }
      disabled={missingOptionsError || (!isFetching && data.length === 0)}
      title={data.length === 0 ? t('app.select.noOptionsAvailable') : undefined}
      label={label ?? filterKey}
      loading={isFetching}
      inlineError={true}
      clearable={clearable}
      onChange={(val) => {
        if (filterData == null) {
          return;
        }
        if (val == null) {
          resetFilterSelections(entityId, filterKey);
          return;
        }
        setFilterSelectionValue(entityId, filterKey, val);
      }}
      placeholder={t('app.pageFilter.placeholder') ?? undefined}
      value={filterData?.selection ?? null}
    />
  );
}
